import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// Connects to data-controller="tom-select"
export default class extends Controller {
  static targets = ["chip"]
  static values = {
    createUrl: String,
    createParam: String,
    createFilter: { type: String, default: "^(?!-)(?!.*-$)(?!.*--)[0-9a-z-]+$" },
    searchUrl: String,
    allowCreate: { type: Boolean, default: false }
  }

  connect() {
    const multiSelect = this.element.querySelector('select[multiple]')
    const singleSelect = this.element.querySelector('select:not([multiple])')

    if (multiSelect) {
      this.initSelect(multiSelect, false)
    } else if (singleSelect) {
      this.initSelect(singleSelect, true)
    }
  }

  disconnect() {
    this.tomSelect?.destroy()
    this.tomSelect = null
  }

  // A suggested-tag chip (#3263): pressed while its tag is among the items.
  // Chips can arrive after init (over the welcome stream, from the drawer's
  // fetch), so they sync on connect as well as on every item change.
  chipTargetConnected() {
    this.#syncChips()
  }

  toggle({ currentTarget, params: { value } }) {
    const id = String(value)
    // Locked while pressing: an added item otherwise opens the dropdown and
    // moves focus into the field, whose preload then reopens it after any
    // close. The lock only gates open(); add and remove go through.
    this.tomSelect.lock()
    if (this.tomSelect.items.includes(id)) {
      this.tomSelect.removeItem(id)
    } else {
      this.tomSelect.addOption({ value: id, text: currentTarget.textContent.trim() })
      this.tomSelect.addItem(id)
    }
    this.tomSelect.unlock()
  }

  #syncChips() {
    if (!this.tomSelect) return
    for (const chip of this.chipTargets) {
      chip.setAttribute("aria-pressed", String(this.tomSelect.items.includes(chip.dataset.tomSelectValueParam)))
    }
  }

  initSelect(selectInput, isSingle) {
    const createUrl = this.createUrlValue
    const createParam = this.createParamValue
    const createFilter = this.createFilterValue
    const syncChips = () => this.#syncChips()

    const options = {
      maxOptions: null,
      persist: false,
      selectOnTab: true,
      hidePlaceholder: true,
      addPrecedence: false,
      // One close point for every pick, typed or by chip (#3263), rather
      // than closeAfterSelect, which acts in onOptionSelect and a full
      // select only. A single (folder) pick is final and blurs; a tags pick
      // keeps the focus so the next keystroke reopens the list (#3458).
      onItemAdd: isSingle
        ? function() { this.setTextboxValue(''); this.close(); this.blur(); }
        : function() { this.setTextboxValue(''); this.close(); syncChips() },
      // Tom Select throttles its input handling: a keystroke stores the value
      // and refreshes the options on a timer. A pick inside that window
      // empties the box, and the timer then opens the whole list the pick
      // just closed (#3497). With no throttle the refresh runs with the
      // keystroke and nothing is pending at the pick.
      refreshThrottle: 0,
      onItemRemove: syncChips,
      create: this.allowCreateValue ? true : createUrl ? async function(input, callback) {
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        if (!token) { callback(); return }

        const data = { [createParam]: { title: input } }
        try {
          const response = await fetch(createUrl, {
            method: 'POST',
            headers: {
              "X-CSRF-Token": token,
              "Content-Type": "application/json",
              "Accept": "application/json"
            },
            body: JSON.stringify(data)
          })
          if (!response.ok) { callback(); return }

          const newItem = await response.json()
          callback({ value: newItem.id, text: newItem.title })
        } catch {
          callback()
        }
      } : false,
    }

    if (isSingle) {
      options.maxItems = 1
      options.allowEmptyOption = true
      options.createFilter = /^.+$/
    } else {
      options.plugins = {
        remove_button: {
          title: 'Remove this item',
        }
      }
      options.createFilter = new RegExp(createFilter)
    }

    if (this.hasSearchUrlValue) {
      const searchUrl = this.searchUrlValue

      options.preload = 'focus'
      options.load = async function(query, callback) {
        try {
          const url = new URL(searchUrl, window.location.origin)
          url.searchParams.set('query', query)

          const response = await fetch(url, { headers: { 'Accept': 'application/json' } })
          if (!response.ok) { callback(); return }

          const results = await response.json()
          callback(results.map(r => ({ value: String(r.id), text: r.title })))
        } catch {
          callback()
        }
      }
    }

    this.tomSelect = new TomSelect(selectInput, options)
  }
}
